
import React, { useEffect, useMemo, useState } from "react";
import type { CampaignCharacter } from "../types";

interface AdvancedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: {
    minTokens: number | null;
    maxTokens: number | null;
    fromDate: string | null;
    toDate: string | null;
    maxConversationCount: number | null;
    minUniqueCharacters: number | null;
    requiredCharacterIds: string[];
  }) => void;
  onReset: () => void;
  initialValues: {
    minTokens: number | null;
    maxTokens: number | null;
    fromDate: string | null;
    toDate: string | null;
    maxConversationCount: number | null;
    minUniqueCharacters: number | null;
    requiredCharacterIds: string[];
  };
  availableCharacters?: CampaignCharacter[];
  isLoading?: boolean;
}

export const AdvancedFilterModal: React.FC<AdvancedFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  initialValues,
  availableCharacters = [],
  isLoading = false,
}) => {
  const [minTokensInput, setMinTokensInput] = useState("");
  const [maxTokensInput, setMaxTokensInput] = useState("");
  const [fromDateInput, setFromDateInput] = useState("");
  const [toDateInput, setToDateInput] = useState("");
  const [maxConversationCountInput, setMaxConversationCountInput] = useState("");
  const [minUniqueCharactersInput, setMinUniqueCharactersInput] = useState("");
  const [selectedCharacterFilterIds, setSelectedCharacterFilterIds] = useState<string[]>([]);
  const [characterSearch, setCharacterSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMinTokensInput(
        typeof initialValues.minTokens === "number"
          ? String(initialValues.minTokens)
          : ""
      );
      setMaxTokensInput(
        typeof initialValues.maxTokens === "number"
          ? String(initialValues.maxTokens)
          : ""
      );
      setFromDateInput(initialValues.fromDate ?? "");
      setToDateInput(initialValues.toDate ?? "");
      setMaxConversationCountInput(
        typeof initialValues.maxConversationCount === "number"
          ? String(initialValues.maxConversationCount)
          : ""
      );
      setMinUniqueCharactersInput(
        typeof initialValues.minUniqueCharacters === "number"
          ? String(initialValues.minUniqueCharacters)
          : ""
      );
      setSelectedCharacterFilterIds(initialValues.requiredCharacterIds ?? []);
      setError(null);
    }
  }, [isOpen, initialValues]);

  const characterNameById = useMemo(() => {
    return availableCharacters.reduce<Record<string, string>>(
      (accumulator, character) => {
        accumulator[character.id] = character.name;
        return accumulator;
      },
      {}
    );
  }, [availableCharacters]);

  const hasCharacterLibrary = availableCharacters.length > 0;

  const filteredCharacterOptions = useMemo(() => {
    if (!hasCharacterLibrary) return [];
    const query = characterSearch.trim().toLowerCase();
    return availableCharacters.filter((character) => {
      if (!query) return true;
      return character.name.toLowerCase().includes(query);
    });
  }, [availableCharacters, characterSearch, hasCharacterLibrary]);

  const isMinUniqueInputDisabled = selectedCharacterFilterIds.length > 0;
  const isCharacterFilterDisabled = minUniqueCharactersInput.trim().length > 0;

  const handleApply = () => {
    const trimmedMin = minTokensInput.trim();
    const trimmedMax = maxTokensInput.trim();
    const parsedMin =
      trimmedMin === "" ? null : Number.parseInt(trimmedMin, 10);
    const parsedMax =
      trimmedMax === "" ? null : Number.parseInt(trimmedMax, 10);
    const trimmedConversationMax = maxConversationCountInput.trim();
    const parsedMaxConversations =
      trimmedConversationMax === ""
        ? null
        : Number.parseInt(trimmedConversationMax, 10);
    const trimmedUniqueCharacters = minUniqueCharactersInput.trim();
    const parsedMinUniqueCharacters =
      trimmedUniqueCharacters === ""
        ? null
        : Number.parseInt(trimmedUniqueCharacters, 10);

    const minInvalid =
      trimmedMin !== "" &&
      (parsedMin === null || Number.isNaN(parsedMin) || parsedMin < 0);
    const maxInvalid =
      trimmedMax !== "" &&
      (parsedMax === null || Number.isNaN(parsedMax) || parsedMax < 0);
    const maxConversationCountInvalid =
      trimmedConversationMax !== "" &&
      (parsedMaxConversations === null ||
        Number.isNaN(parsedMaxConversations) ||
        parsedMaxConversations < 0);
    const minUniqueCharactersInvalid =
      trimmedUniqueCharacters !== "" &&
      (parsedMinUniqueCharacters === null ||
        Number.isNaN(parsedMinUniqueCharacters) ||
        parsedMinUniqueCharacters < 0);

    if (minInvalid || maxInvalid) {
      setError(
        "Enter positive whole numbers for minimum and maximum tokens."
      );
      return;
    }

    if (maxConversationCountInvalid) {
      setError("Enter whole numbers for conversation filters.");
      return;
    }
    
    if (minUniqueCharactersInvalid) {
        setError(
            "Enter a whole number for unique character filters."
        );
        return;
    }

    if (parsedMin !== null && parsedMax !== null && parsedMin > parsedMax) {
      setError("Minimum tokens must be less than the maximum.");
      return;
    }

    const normalizedFrom = fromDateInput ? fromDateInput : null;
    const normalizedTo = toDateInput ? toDateInput : null;

    if (normalizedFrom && normalizedTo) {
      const fromDate = new Date(normalizedFrom);
      const toDate = new Date(normalizedTo);
      if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
        setError("Enter valid dates for the range.");
        return;
      }
      if (fromDate > toDate) {
        setError("The start date must be before the end date.");
        return;
      }
    }

    setError(null);
    const sanitizedCharacterIds = Array.from(
      new Set(selectedCharacterFilterIds.map((id) => id.trim()).filter(Boolean))
    );
    onApply({
      minTokens: parsedMin,
      maxTokens: parsedMax,
      fromDate: normalizedFrom,
      toDate: normalizedTo,
      maxConversationCount: parsedMaxConversations,
      minUniqueCharacters: parsedMinUniqueCharacters,
      requiredCharacterIds: sanitizedCharacterIds,
    });
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  const handleMinUniqueCharactersChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextValue = event.target.value;
    setMinUniqueCharactersInput(nextValue);
    setError(null);
    if (nextValue.trim().length > 0 && selectedCharacterFilterIds.length) {
      setSelectedCharacterFilterIds([]);
    }
  };

  const toggleCharacterFilter = (characterId: string) => {
    if (isCharacterFilterDisabled) {
      return;
    }
    setSelectedCharacterFilterIds((current) => {
      if (current.includes(characterId)) {
        return current.filter((id) => id !== characterId);
      }
      const updated = [...current, characterId];
      if (updated.length > 0 && minUniqueCharactersInput.trim().length > 0) {
        setMinUniqueCharactersInput("");
      }
      return updated;
    });
    setError(null);
  };

  const handleManualCharacterEntry = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    if (isCharacterFilterDisabled) {
      return;
    }
    const tokens = event.target.value
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean);
    setSelectedCharacterFilterIds(tokens);
    setError(null);
    if (tokens.length > 0 && minUniqueCharactersInput.trim().length > 0) {
      setMinUniqueCharactersInput("");
    }
  };

  const handleRemoveCharacterFilter = (characterId: string) => {
    setSelectedCharacterFilterIds((current) =>
      current.filter((id) => id !== characterId)
    );
    setError(null);
  };

  const handleClearCharacterFilters = () => {
    setSelectedCharacterFilterIds([]);
    setError(null);
  };
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
    <div
      className="absolute inset-0 bg-gray-900/30 backdrop-blur-sm"
      onClick={onClose}
    ></div>
    <div className="relative z-50 w-full max-w-5xl rounded-3xl bg-white shadow-2xl">
      <div className="flex items-start justify-between border-b border-gray-100 p-5">
        <div>
          <div className="text-lg font-semibold text-gray-900">
            Advanced filters
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-gray-200 p-2 text-gray-500 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
          aria-label="Close advanced filters"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m6 6 8 8M14 6l-8 8"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto p-5">
        <div className="space-y-5">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-sm">
                <div className="text-sm font-semibold text-gray-900">
                  Usage & timeframe
                </div>
                <p className="text-xs text-gray-500">
                  Narrow by spend or choose a window based on last
                  interaction.
                </p>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:max-w-3xl">
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Min tokens spent
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    value={minTokensInput}
                    onChange={(e) => setMinTokensInput(e.target.value)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Max tokens spent
                  <input
                    type="number"
                    min="0"
                    placeholder="Any"
                    value={maxTokensInput}
                    onChange={(e) => setMaxTokensInput(e.target.value)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  From date
                  <input
                    type="date"
                    value={fromDateInput}
                    onChange={(e) => setFromDateInput(e.target.value)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  To date
                  <input
                    type="date"
                    value={toDateInput}
                    onChange={(e) => setToDateInput(e.target.value)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-sm">
                <div className="text-sm font-semibold text-gray-900">
                  Conversation depth
                </div>
                <p className="text-xs text-gray-500">
                  Zero in on loyal fans or brand new readers using chat
                  counts.
                </p>
              </div>
              <div className="grid w-full gap-3 sm:grid-cols-2 lg:max-w-3xl">
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Max conversations
                  <input
                    type="number"
                    min="0"
                    placeholder="Any"
                    value={maxConversationCountInput}
                    onChange={(e) => setMaxConversationCountInput(e.target.value)}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:col-span-2">
                  Min unique characters chatted
                  <input
                    type="number"
                    min="0"
                    placeholder="Any"
                    value={minUniqueCharactersInput}
                    onChange={handleMinUniqueCharactersChange}
                    disabled={isMinUniqueInputDisabled}
                    className={`rounded-xl border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] ${
                        isMinUniqueInputDisabled
                        ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                        : "border-gray-200 text-gray-700"
                    }`}
                  />
                </label>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-sm">
                <div className="text-sm font-semibold text-gray-900">
                  Character filters
                </div>
                <p className="text-xs text-gray-500">
                  Focus on users who chatted with specific characters or
                  combos.
                </p>
              </div>
              <div className="flex w-full flex-col gap-4 lg:max-w-3xl">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Search characters"
                      value={characterSearch}
                      onChange={(event) =>
                        setCharacterSearch(event.target.value)
                      }
                      disabled={isCharacterFilterDisabled}
                      className={`w-full rounded-xl border px-3 py-2 pl-10 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] ${
                        isCharacterFilterDisabled
                          ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "border-gray-200 text-gray-700"
                      }`}
                    />
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-gray-400">
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9 3.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <path
                          d="m15.5 15.5-2.5-2.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearCharacterFilters}
                    disabled={!selectedCharacterFilterIds.length}
                    className="rounded-full border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Clear selections
                  </button>
                </div>
                {hasCharacterLibrary ? (
                  <div className="max-h-48 overflow-y-auto rounded-2xl border border-dashed border-gray-200 p-2">
                    {filteredCharacterOptions.length ? (
                      <div className="flex flex-wrap gap-2">
                        {filteredCharacterOptions.map((character) => {
                          const isSelected =
                            selectedCharacterFilterIds.includes(
                              character.id
                            );
                          return (
                            <button
                              key={character.id}
                              type="button"
                              onClick={() =>
                                toggleCharacterFilter(character.id)
                              }
                              disabled={isCharacterFilterDisabled}
                              className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                isSelected
                                  ? "border-[var(--primary-color)] bg-[var(--primary-color)] text-white shadow-sm"
                                  : "border-gray-200 bg-gray-50 text-gray-600 hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
                              } ${
                                isCharacterFilterDisabled
                                  ? "cursor-not-allowed opacity-60"
                                  : ""
                              }`}
                            >
                              {character.name}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="px-2 py-1 text-xs text-gray-400">
                        No characters match that search.
                      </p>
                    )}
                  </div>
                ) : (
                  <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Characters (comma separated)
                    <textarea
                      rows={3}
                      placeholder="aurora, cage, andie"
                      value={selectedCharacterFilterIds.join(", ")}
                      onChange={handleManualCharacterEntry}
                      disabled={isCharacterFilterDisabled}
                      className={`rounded-xl border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] ${
                        isCharacterFilterDisabled
                          ? "border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                          : "border-gray-200 text-gray-700"
                      }`}
                    />
                  </label>
                )}
                <div className="space-y-2 text-xs text-gray-500">
                  <p>
                    Users must have chatted with every selected
                    character. Leave the picker empty to include anyone.
                  </p>
                  {isCharacterFilterDisabled ? (
                    <p className="rounded-xl bg-amber-50 px-3 py-2 text-amber-700">
                      Character picker disabled while "Min unique
                      characters" has a value. Clear that input to
                      select specific characters instead.
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedCharacterFilterIds.length ? (
                    selectedCharacterFilterIds.map((characterId) => (
                      <span
                        key={characterId}
                        className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
                      >
                        <span className="font-semibold text-gray-900">
                          {characterNameById[characterId] ??
                            characterId}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            handleRemoveCharacterFilter(characterId)
                          }
                          className="text-gray-400 transition hover:text-red-500"
                          aria-label="Remove character filter"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-400">
                      No character constraint applied.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {error ? (
          <p className="mt-4 text-xs font-semibold text-red-600">
            {error}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 p-5">
        <button
          type="button"
          onClick={handleReset}
          className="rounded-full border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
        >
          Reset all filters
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={isLoading}
            className="rounded-full border border-gray-200 bg-[var(--primary-color)] px-5 py-2 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? "Applying…" : "Apply filters"}
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};
